"use client";

import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { useAccount, usePublicClient } from "wagmi";
import { MINER_ABI } from "@/contract/abi/miner";
import { WrapperABI } from "@/contract/abi/wrapper";
import ContractAddress from "@/contract/addresses";
import { useGuardedWriteContract } from "@/hooks/auth/useGuardedWrite";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";

export interface ConvertAssetParams {
  collectionAddress: `0x${string}`;
  tokenId: string;
  convertToNFT: boolean;
  wrapperTokenId?: string;
}

interface UseConvertAssetOptions {
  onSuccess?: () => void;
}

export const useConvertAsset = (options?: UseConvertAssetOptions) => {
  const t = useTranslations("modals.convert");
  const { handleContractError } = useContractErrorHandler();
  const { address } = useAccount();

  const publicClient = usePublicClient();

  const { writeContractAsync } = useGuardedWriteContract();

  const mutationFn = async (params: ConvertAssetParams) => {
    if (!address || !publicClient) {
      throw new Error(t("toasts.walletNotConnected"));
    }

    const { collectionAddress, tokenId, convertToNFT, wrapperTokenId } = params;

    const waitForTransaction = async (txHash: `0x${string}`) => {
      const receipt = await publicClient.waitForTransactionReceipt({
        hash: txHash,
      });
      if (
        receipt.status === "reverted" ||
        (typeof receipt.status === "number" && receipt.status === 0)
      ) {
        throw new Error("Transaction reverted");
      }
      return receipt;
    };

    if (convertToNFT) {
      if (!wrapperTokenId) {
        throw new Error(t("toasts.tokenIdRequired"));
      }

      const txHash = await writeContractAsync({
        address: ContractAddress.WRAPPER,
        abi: WrapperABI,
        functionName: "withdraw",
        args: [collectionAddress, BigInt(wrapperTokenId)],
      });
      await waitForTransaction(txHash);
    } else {
      const isApproved = await publicClient.readContract({
        address: collectionAddress,
        abi: MINER_ABI,
        functionName: "getApproved",
        args: [BigInt(tokenId)],
      });

      if (isApproved !== ContractAddress.WRAPPER) {
        const approveTxHash = await writeContractAsync({
          address: collectionAddress,
          abi: MINER_ABI,
          functionName: "approve",
          args: [ContractAddress.WRAPPER, BigInt(tokenId)],
        });
        await waitForTransaction(approveTxHash);
      }

      // Mint wrapper token
      const txHash = await writeContractAsync({
        address: ContractAddress.WRAPPER,
        abi: WrapperABI,
        functionName: "mint",
        args: [collectionAddress, address, BigInt(tokenId)],
      });
      await waitForTransaction(txHash);
    }
  };

  return useMutation({
    mutationFn,
    mutationKey: ["convert-asset"],
    onError: (error) => {
      handleContractError(error);
    },
    onSuccess: () => {
      toast.success(t("toasts.success"));
      options?.onSuccess?.();
    },
  });
};
